import React from 'react';
import {useLsValue} from '../../../../../hooks/use-ls-value';
import {LS_PREFIX} from '../../../../../providers/global-state';
import {GraphPresentation, GraphPresentationDetails} from './interface';
import {StructuredGraph} from '../../../../../components/graph/graph-logic/structured-graph';
import {getNodesFromRawNodes} from './helpers';

interface Props {
    queryId: number;
    selectedPresentation: GraphPresentation;
    selectedPresentationDetails: GraphPresentationDetails;
    handlePresentationEdited: () => void;
    structuredGraph?: StructuredGraph;
}

export function useHiddenNodes(props: Props) {
    const {
        queryId,
        selectedPresentation,
        selectedPresentationDetails,
        handlePresentationEdited,
        structuredGraph
    } = props;

    const [hiddenRawNodes, setHiddenRawNodes, clearHiddenRawNodes] = useLsValue<
        string,
        [number, number],
        []
    >({
        changeEntityParams: [queryId, selectedPresentation.id],
        getKey: ([qId, pId]) =>
            `${LS_PREFIX}.queryGraph.${qId}.presentation${pId}.hiddenExplicitlyNodes`,
        rewriteKeyParams: [],
        defaultValue: JSON.stringify(selectedPresentationDetails.hiddenNodes),
        validateValue: (value) => !JSON.parse(value).map(Number).some(Number.isNaN)
    });

    const hiddenNodesParsed = React.useMemo(
        () => getNodesFromRawNodes(JSON.parse(hiddenRawNodes), structuredGraph),
        [hiddenRawNodes, structuredGraph]
    );

    const [isHiddenNodesVisible, setIsHiddenNodesVisible] = React.useState(false);
    const toggleIsHiddenNodesVisible = React.useCallback(() => {
        setIsHiddenNodesVisible(!isHiddenNodesVisible);
    }, [setIsHiddenNodesVisible, isHiddenNodesVisible]);

    const isHiddenNode = React.useCallback(
        (nodeId: number) => new Set(hiddenNodesParsed).has(nodeId),
        [hiddenNodesParsed]
    );

    const isHiddenEdge = React.useCallback(
        (edgeId: number) => {
            // we assume that when you need this function, you already have your structuredGraph
            // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
            const edge = structuredGraph?.getEdge(edgeId)!;
            return isHiddenNode(edge.sourceNode.id) || isHiddenNode(edge.targetNode.id);
        },
        [structuredGraph, isHiddenNode]
    );

    return {
        hiddenNodes: hiddenNodesParsed,
        hiddenRawNodeIds: React.useMemo(
            () => JSON.parse(hiddenRawNodes).map(Number),
            [hiddenRawNodes]
        ),
        setHiddenRawNodeIds: React.useCallback(
            (arr: number[]) => {
                setHiddenRawNodes(JSON.stringify(arr));
            },
            [setHiddenRawNodes]
        ),
        setHiddenNodes: React.useCallback(
            (arr: number[]) => {
                handlePresentationEdited();
                let rawIds: number[] = [];
                arr.forEach((nodeId) => {
                    rawIds = [...rawIds, ...structuredGraph!.getNode(nodeId)!.rawNodeIds];
                });
                setHiddenRawNodes(JSON.stringify(rawIds));
            },
            [setHiddenRawNodes, handlePresentationEdited, structuredGraph]
        ),
        clearHiddenNodes: clearHiddenRawNodes,
        isHiddenNodesVisible,
        toggleIsHiddenNodesVisible,
        isHiddenNode,
        isHiddenEdge
    };
}
